import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Building2, FileText, Upload, Calendar, User, Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import { 
  useGetVendorQuery, 
  useGetDocumentsQuery, 
  useGetVendorDocumentsQuery,
  useBulkUploadDocumentsVendorBasedMutation,
  useRenameVendorMutation
} from '../store/api/apiSlice';
import { useSelector } from 'react-redux';
import { useGetCurrentUserQuery } from '../store/api/apiSlice';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ChatPanel } from '../components/ui/ChatPanel';
import { useStartVendorChatMutation, useSendVendorChatMessageMutation } from '../store/api/apiSlice';

export const VendorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<FileList | null>(null);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameName, setRenameName] = useState('');

  // Get auth state
  const auth = useSelector((state: any) => state.auth);
  const isAuthReady = auth.isAuthenticated && !auth.isLoading && auth.token;
  const { data: currentUser } = useGetCurrentUserQuery(undefined, { skip: !isAuthReady });

  // API hooks
  const { data: vendor, isLoading: vendorLoading, error: vendorError } = useGetVendorQuery(id!, {
    skip: !isAuthReady || !id
  });

  const { data: documentsData, isLoading: documentsLoading } = useGetDocumentsQuery({
    vendor_id: id,
    limit: 10
  }, {
    skip: !isAuthReady || !id
  });

  const [bulkUploadVendorBased, { isLoading: uploading }] = useBulkUploadDocumentsVendorBasedMutation();
  const [startVendorChat] = useStartVendorChatMutation();
  const [sendVendorChatMessage] = useSendVendorChatMessageMutation();
  const [renameVendor, { isLoading: isRenaming }] = useRenameVendorMutation();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState<string | undefined>(undefined);

  const handleFileUpload = async () => {
    if (!uploadFiles || !id) return;

    const formData = new FormData();
    for (let i = 0; i < uploadFiles.length; i++) {
      formData.append('files', uploadFiles[i]);
    }
    formData.append('vendor_name', vendor?.name || '');
    formData.append('create_vendor_if_not_exists', 'false'); // Don't create new vendor
    formData.append('vendor_detection_enabled', 'false'); // Don't reassign to different vendor

    try {
      await bulkUploadVendorBased(formData).unwrap();
      setIsUploadModalOpen(false);
      setUploadFiles(null);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleOpenChat = async () => {
    if (!id) return;
    try {
      setIsChatOpen(true);
      setChatMessage('Loading context...');
      const res = await startVendorChat({ vendorId: id }).unwrap();
      setChatMessage(res.message || 'Chat session is ready.');
    } catch (e) {
      setChatMessage('Failed to start chat session.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const openRename = () => {
    if (!vendor) return;
    setRenameName(vendor.name);
    setIsRenameModalOpen(true);
  };

  const handleRename = async () => {
    if (!id) return;
    const trimmed = renameName.trim();
    if (!trimmed) return;
    try {
      await renameVendor({ id, new_name: trimmed }).unwrap();
      setIsRenameModalOpen(false);
    } catch (e) {
      // Optionally surface error
      console.error('Rename failed:', e);
    }
  };

  const getRiskLevelColor = (riskLevel: string | undefined) => {
    switch (riskLevel) {
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'high':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'critical':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  if (vendorLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (vendorError || !vendor) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 dark:text-red-400 mb-4">
          Vendor not found
        </div>
        <button
          onClick={() => window.history.back()}
          className="btn-secondary"
        >
          Go Back
        </button>
      </div>
    );
  }

  const documents = documentsData?.items || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
            <Building2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {vendor.name}
            </h1>
            <div className="flex items-center space-x-3 mt-2">
              <span className="inline-block px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                {vendor.vendor_type || 'vendor'}
              </span>
              {vendor.risk_level && (
                <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${getRiskLevelColor(vendor.risk_level)}`}>
                  {vendor.risk_level} risk
                </span>
              )}
              {vendor.compliance_status && (
                <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${
                  vendor.compliance_status === 'approved' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                }`}>
                  {vendor.compliance_status}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="btn-primary"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Contracts
          </button>
          {(currentUser?.claims?.some(
            (claim) => claim.claim_type === 'superadmin' || 
              (claim.claim_type === 'tenant_admin' && claim.resource_id === currentUser.tenant_id)
          )) && (
            <button onClick={openRename} className="btn-secondary">Rename</button>
          )}
          <button onClick={handleOpenChat} className="btn-secondary">Chat about this vendor</button>
        </div>
      </div>

      {/* Vendor Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {vendor.description && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Description
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {vendor.description}
              </p>
            </div>
          )}

          {/* Recent Contracts */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Recent Contracts
              </h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {vendor.document_count || 0} total
              </span>
            </div>
            {documentsLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="md" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 dark:text-gray-400">No contracts yet</p>
                <button
                  onClick={() => setIsUploadModalOpen(true)}
                  className="btn-primary mt-3"
                >
                  Upload First Contract
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">
                          {doc.title || doc.original_filename}
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {doc.contract_type} • {formatDate(doc.created_at)}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      doc.processing_status === 'completed' || doc.processing_status === 'analyzed'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : doc.processing_status === 'processing'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : doc.processing_status === 'failed'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                    }`}>
                      {doc.processing_status}
                    </span>
                  </div>
                ))}
                {vendor.document_count && vendor.document_count > documents.length && (
                  <button className="w-full btn-secondary">
                    View All Contracts ({vendor.document_count})
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Information */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Contact Information
            </h3>
            <div className="space-y-3">
              {vendor.contact_name && (
                <div className="flex items-center space-x-3">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400">{vendor.contact_name}</span>
                </div>
              )}
              {vendor.contact_email && (
                <div className="flex items-center space-x-3">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <a 
                    href={`mailto:${vendor.contact_email}`}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {vendor.contact_email}
                  </a>
                </div>
              )}
              {vendor.contact_phone && (
                <div className="flex items-center space-x-3">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <a 
                    href={`tel:${vendor.contact_phone}`}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {vendor.contact_phone}
                  </a>
                </div>
              )}
              {(vendor.address_line1 || vendor.city || vendor.state) && (
                <div className="flex items-start space-x-3">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div className="text-gray-600 dark:text-gray-400">
                    {vendor.address_line1 && <div>{vendor.address_line1}</div>}
                    {vendor.address_line2 && <div>{vendor.address_line2}</div>}
                    <div>
                      {vendor.city && `${vendor.city}`}
                      {vendor.city && vendor.state && ', '}
                      {vendor.state && `${vendor.state}`}
                      {vendor.postal_code && ` ${vendor.postal_code}`}
                    </div>
                    {vendor.country && <div>{vendor.country}</div>}
                  </div>
                </div>
              )}
              {vendor.website && (
                <div className="flex items-center space-x-3">
                  <ExternalLink className="w-4 h-4 text-gray-400" />
                  <a 
                    href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {vendor.website}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Company Details */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Company Details
            </h3>
            <div className="space-y-3 text-sm">
              {vendor.industry && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Industry:</span>
                  <span className="ml-2 text-gray-900 dark:text-gray-100">{vendor.industry}</span>
                </div>
              )}
              {vendor.company_size && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Size:</span>
                  <span className="ml-2 text-gray-900 dark:text-gray-100">{vendor.company_size}</span>
                </div>
              )}
              {vendor.tax_id && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Tax ID:</span>
                  <span className="ml-2 text-gray-900 dark:text-gray-100">{vendor.tax_id}</span>
                </div>
              )}
              <div>
                <span className="text-gray-500 dark:text-gray-400">Added:</span>
                <span className="ml-2 text-gray-900 dark:text-gray-100">{formatDate(vendor.created_at)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Upload Contracts for {vendor.name}
            </h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Files
              </label>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.txt"
                onChange={(e) => setUploadFiles(e.target.files)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                PDF, DOC, DOCX, TXT files only. Max 50 files.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setIsUploadModalOpen(false);
                  setUploadFiles(null);
                }}
                className="btn-secondary"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={handleFileUpload}
                disabled={!uploadFiles || uploadFiles.length === 0 || uploading}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {uploading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Uploading...</span>
                  </>
                ) : (
                  'Upload Contracts'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {isRenameModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Rename Vendor</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New Name</label>
              <input
                type="text"
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                placeholder="Enter new vendor name"
                disabled={isRenaming}
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsRenameModalOpen(false)}
                className="btn-secondary"
                disabled={isRenaming}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRename}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                disabled={isRenaming || !renameName.trim()}
              >
                {isRenaming ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Renaming...</span>
                  </>
                ) : (
                  'Rename'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ChatPanel
        isOpen={isChatOpen}
        title={vendor ? `${vendor.name} – Vendor Chat` : 'Vendor Chat'}
        initialMessage={chatMessage}
        onClose={() => setIsChatOpen(false)}
        onSend={async (message, history) => {
          if (!id) return '';
          const res = await sendVendorChatMessage({ vendorId: id, message, history }).unwrap();
          return res.reply;
        }}
      />
    </div>
  );
};